import React, { useEffect, useRef } from "react"
import { View, Text, StyleSheet, Animated, Easing } from "react-native"
import { LinearGradient } from "expo-linear-gradient"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"
import { SUBJECT_GOALS, goalIndexOf } from "./funnelSubjectGoals"

/**
 * L'étiquette d'un objectif visé : ses étoiles et son nom sur un dégradé —
 * bleu de la marque jusqu'à « Solide », or à partir d'« Ambitieux ». Le même
 * badge sur le curseur du compositeur et sur le bloc de matière du programme :
 * c'est là qu'on rend au parent le choix qu'il vient de faire.
 */
const FunnelGoalBadge = ({
    goal,
    size = "md",
    fillStars = false,
    delay = 0,
}) => {
    const index = goalIndexOf(goal?.slug ?? goal)
    if (index < 0) return null
    const isHigh = index >= 2
    const starSize = size === "sm" ? 8 : 9

    return (
        <LinearGradient
            colors={
                isHigh
                    ? [theme.colors.success, theme.colors.successDark]
                    : [
                          theme.colors.primary,
                          theme.colors.primaryDark || theme.colors.primary,
                      ]
            }
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[styles.badge, size === "sm" && styles.badgeSm]}
        >
            <View style={styles.stars}>
                {SUBJECT_GOALS.map((g, i) => (
                    <Star
                        key={g.slug}
                        lit={i <= index}
                        size={starSize}
                        // Les étoiles acquises se remplissent une à une à
                        // l'arrivée du programme : le choix du parent se rejoue.
                        animate={fillStars && i <= index}
                        delay={delay + i * 120}
                    />
                ))}
            </View>
            <Text style={[styles.text, size === "sm" && styles.textSm]}>
                {SUBJECT_GOALS[index].name}
            </Text>
        </LinearGradient>
    )
}

const Star = ({ lit, size, animate, delay }) => {
    const v = useRef(new Animated.Value(animate ? 0 : 1)).current
    useEffect(() => {
        if (!animate) return
        Animated.timing(v, {
            toValue: 1,
            duration: 220,
            delay,
            easing: Easing.out(Easing.back(1.6)),
            useNativeDriver: true,
        }).start()
    }, [animate, delay, v])
    return (
        <View>
            <FontAwesome5
                name="star"
                size={size}
                color="rgba(255,255,255,0.4)"
            />
            {lit ? (
                <Animated.View
                    style={[
                        StyleSheet.absoluteFill,
                        { opacity: v, transform: [{ scale: v }] },
                    ]}
                >
                    <FontAwesome5
                        name="star"
                        solid
                        size={size}
                        color={theme.colors.white}
                    />
                </Animated.View>
            ) : null}
        </View>
    )
}

const styles = StyleSheet.create({
    badge: {
        flexDirection: "row",
        alignItems: "center",
        gap: 7,
        borderRadius: 999,
        paddingHorizontal: 11,
        paddingVertical: 5,
    },
    badgeSm: { gap: 6, paddingHorizontal: 10 },
    stars: { flexDirection: "row", gap: 1.5 },
    text: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 11.5,
        color: theme.colors.white,
        letterSpacing: 0.2,
    },
    textSm: { fontSize: 11, letterSpacing: 0 },
})

export default FunnelGoalBadge
